/* ============================================================
   A1 — Corridor Health Scorecard

   Audit fixes applied here:

   A-06  Results render BEFORE persistence, and persistence cannot throw
         into the render path. Previously `await idb.put(...)` sat above
         the first paint inside an un-awaited async function, so a quota
         failure or Safari private browsing meant 32 questions answered
         and a blank screen.

   A-07  Unanswered domains are excluded from the index rather than
         scored 0% at full weight. Weights are renormalised across the
         domains that were actually answered, and a confidence figure is
         reported next to the score.

   A-08  Options carrying `score: null` ("not applicable") are removed
         from the denominator instead of counting as 2/4.

   A-11  The auto-advance timer is cancelled on every selection, so
         changing your mind inside the 400 ms window no longer queues two
         advances and skips a question.
   ============================================================ */

import {
  DOMAINS,
  getAllQuestions,
  getTotalQuestions,
  getQuestionAtIndex,
  getDomainById
} from '../../data/domains.js';
import { getRegulation, statusOf, STATUS_META } from '../../data/regulations.js';
import { session } from '../../shared/storage.js';
import { saveRecord } from '../../shared/records.js';
import { router } from '../../shared/router.js';
import { icon } from '../../shared/icons.js';
import { html, raw } from '../../shared/html.js';
import { percent, shortDate } from '../../shared/format.js';
import { Progress, update as updateProgress } from '../../components/Progress.js';
import { OptionCard } from '../../components/OptionCard.js';
import { ScoreRing } from '../../components/ScoreRing.js';
import { DomainBar } from '../../components/DomainBar.js';
import { FindingCard } from '../../components/FindingCard.js';
import { PrintButton } from '../../components/PrintButton.js';
import {
  OPERATOR_TYPES,
  DEFAULT_OPERATOR,
  weightsFor,
  describeCalibration
} from '../../data/calibration.js';

const KEY_ANSWERS = 'scorecard_answers';
const KEY_CURRENT = 'scorecard_current';
const KEY_OPERATOR = 'scorecard_operator';
const AUTO_ADVANCE_MS = 420;

/** Module-scoped so a re-render cannot orphan a pending timer (A-11). */
let advanceTimer = null;

function cancelAdvance() {
  if (advanceTimer) clearTimeout(advanceTimer);
  advanceTimer = null;
}

export function ScorecardPage() {
  const page = document.createElement('div');
  page.className = 'container-narrow page';

  // The only live tool that shipped without an h1 — the flow went
  // straight into question one, so the pre-rendered page a crawler and a
  // screen reader both meet had no title at all. Visually hidden because
  // the progress bar and the question are the right thing to see first;
  // the heading is for the document, not the layout.
  const title = document.createElement('h1');
  title.className = 'sr-only';
  title.textContent = 'Corridor Health Scorecard';
  page.appendChild(title);

  const state = {
    total: getTotalQuestions(),
    current: Number(session.get(KEY_CURRENT, 0)) || 0,
    answers: new Map(session.get(KEY_ANSWERS, [])),
    operator: session.get(KEY_OPERATOR, DEFAULT_OPERATOR) || DEFAULT_OPERATOR
  };

  const progress = Progress({
    total: state.total,
    current: state.current,
    answered: state.answers.size,
    domainName: getQuestionAtIndex(state.current)?.domainName
  });
  progress.id = 'scorecard-progress';

  const content = document.createElement('div');
  content.id = 'scorecard-content';

  const calibration = OperatorPicker(state.operator, (id) => {
    state.operator = id;
    session.set(KEY_OPERATOR, id);
    /* Re-render whichever view is showing: on the results screen the
       index itself changes, and on a question screen the domain weight
       shown beside the heading does. Silently recalculating behind a
       stale screen is how a user ends up quoting a number the page is
       no longer showing. */
    if (state.current >= state.total) renderResults(view);
    else renderQuestion(view);
  });

  page.append(calibration, progress, content);

  const view = {
    page,
    progress,
    content,
    state,
    goTo: (index) => goToQuestion(view, index),
    select: (qIndex, optIndex) => selectOption(view, qIndex, optIndex),
    restart: () => restart(view)
  };

  if (state.current >= state.total) renderResults(view);
  else renderQuestion(view);

  return page;
}

/* ============================================================
   Operator calibration
   ============================================================ */

/* A <select>, not four cards. The choice is a qualifier on the
   instrument rather than a step in it, and four large options above
   question one would read as the first question — which it is not, and
   which would push the actual first question below the fold on a
   phone. */
function OperatorPicker(current, onChange) {
  const wrap = document.createElement('div');
  wrap.className = 'card card-tinted no-print';
  wrap.style.marginBottom = 'var(--ca-space-4)';

  const id = 'scorecard-operator';
  const options = OPERATOR_TYPES.map(
    (t) =>
      `<option value="${t.id}"${t.id === current ? ' selected' : ''}>${t.label} — ${t.hint}</option>`
  ).join('');

  wrap.innerHTML = html`
    <label for="${id}" style="display:block; font-size: var(--ca-text-sm); font-weight: var(--ca-weight-medium); margin-bottom: var(--ca-space-2);">
      What kind of operator are you?
    </label>
    <select id="${id}" class="select" style="width:100%;">${raw(options)}</select>
    <p id="${id}-effect" style="font-size: var(--ca-text-xs); color: var(--ca-text-secondary); margin: var(--ca-space-2) 0 0;"></p>
  `;

  const select = wrap.querySelector('select');
  const effect = wrap.querySelector(`#${id}-effect`);

  /* Says what the choice did, in domain names and points. A picker that
     silently alters the weighting is a hidden variable in a number the
     user is about to put in front of a lender. */
  const describe = () => {
    const c = describeCalibration(select.value);
    if (!c) {
      effect.textContent =
        'Domains are weighted as published — the base instrument. Choosing a type re-weights ' +
        'them for how that operator actually works, and says what it changed.';
      return;
    }
    const parts = c.moved.map((m) => `${m.name} ${m.from}% → ${m.to}%`).join(' · ');
    effect.textContent = `Weighted for a ${c.label.toLowerCase()}: ${parts}. Totals still come to 100%.`;
  };
  describe();

  select.addEventListener('change', () => {
    describe();
    onChange(select.value);
  });

  return wrap;
}

/* ============================================================
   Question view
   ============================================================ */

function renderQuestion(view) {
  const { content, state } = view;
  const qIndex = state.current;
  const question = getQuestionAtIndex(qIndex);

  if (!question) {
    renderResults(view);
    return;
  }

  const domain = getDomainById(question.domainId);
  const selected = state.answers.get(qIndex);

  content.className = 'animate-fade-in';
  content.innerHTML = html`
    <div
      class="badge"
      style="background: color-mix(in srgb, ${domain.color} 12%, transparent); color: ${domain.color}; margin-bottom: var(--ca-space-3);"
    >
      ${domain.name} · ${domain.weight}% of the index
    </div>
    <h2 style="font-size: var(--ca-text-md); margin-bottom: var(--ca-space-4);">${question.text}</h2>
    <div id="reg-context"></div>
    ${question.note
      ? html`<p style="font-size: var(--ca-text-xs); color: var(--ca-text-quaternary); margin-bottom: var(--ca-space-3);">
          ${question.note}
        </p>`
      : ''}
    <div class="stack" id="options" style="gap: var(--ca-space-2);"></div>
    <div class="row" style="margin-top: var(--ca-space-5); gap: var(--ca-space-2);">
      <button class="btn btn-secondary" id="back-btn" type="button">
        ${raw(icon('left', 16))}<span>Back</span>
      </button>
      <div class="spacer"></div>
      <button class="btn btn-primary" id="next-btn" type="button"></button>
    </div>
  `;

  /* Live regulatory status, rather than baked-in future tense. */
  const regSlot = content.querySelector('#reg-context');
  if (question.regulationId) {
    const reg = getRegulation(question.regulationId);
    if (reg) {
      const status = statusOf(reg);
      const meta = STATUS_META[status];
      const tone =
        status === 'in-force' ? 'warning' : status === 'imminent' ? 'warning' : 'info';
      const box = document.createElement('div');
      box.className = `notice notice-${tone}`;
      box.style.marginBottom = 'var(--ca-space-4)';
      box.innerHTML = html`
        <span style="flex-shrink:0; margin-top:2px;">${raw(icon('info', 15))}</span>
        <div style="font-size: var(--ca-text-xs);">
          <strong style="font-weight: var(--ca-weight-medium);">${reg.name}</strong> —
          ${meta.label.toLowerCase()} since ${shortDate(reg.effectiveDate)} (${reg.authority}).
          ${reg.cost ? html` Cost: ${reg.cost}.` : ''}
        </div>
      `;
      regSlot.appendChild(box);
    }
  }

  const optionsWrap = content.querySelector('#options');
  question.options.forEach((option, i) => {
    optionsWrap.appendChild(
      OptionCard({
        label: option.label,
        selected: selected === i,
        notApplicable: option.score === null,
        onClick: () => view.select(qIndex, i)
      })
    );
  });

  const backBtn = content.querySelector('#back-btn');
  backBtn.style.visibility = qIndex === 0 ? 'hidden' : 'visible';
  backBtn.addEventListener('click', () => {
    cancelAdvance();
    view.goTo(qIndex - 1);
  });

  const nextBtn = content.querySelector('#next-btn');
  const isLast = qIndex === state.total - 1;
  nextBtn.innerHTML = isLast ? '<span>See results</span>' : `<span>Next</span>${icon('right', 16)}`;
  nextBtn.disabled = selected === undefined;
  nextBtn.addEventListener('click', () => {
    cancelAdvance();
    view.goTo(qIndex + 1);
  });

  updateProgress(view.progress, {
    total: state.total,
    current: qIndex,
    answered: state.answers.size,
    domainName: question.domainName
  });
}

function selectOption(view, qIndex, optIndex) {
  cancelAdvance();

  view.state.answers.set(qIndex, optIndex);
  session.set(KEY_ANSWERS, [...view.state.answers.entries()]);

  renderQuestion(view);

  const isLast = qIndex >= view.state.total - 1;
  if (!isLast) {
    advanceTimer = setTimeout(() => {
      advanceTimer = null;
      view.goTo(qIndex + 1);
    }, AUTO_ADVANCE_MS);
  }
}

function goToQuestion(view, index) {
  const next = Math.max(0, Math.min(index, view.state.total));
  view.state.current = next;
  session.set(KEY_CURRENT, next);

  if (next >= view.state.total) renderResults(view);
  else renderQuestion(view);
}

function restart(view) {
  cancelAdvance();
  session.remove(KEY_ANSWERS);
  session.remove(KEY_CURRENT);
  view.state.answers = new Map();
  view.state.current = 0;
  view.progress.style.display = '';
  renderQuestion(view);
}

/* ============================================================
   Scoring
   ============================================================ */

/**
 * @param {Map} answers
 * @param {string} [operatorId] operator type whose weights apply; the
 *   base weights are used when it is absent or unrecognised, so an old
 *   saved run scored before calibration existed still computes.
 */
export function calculateResults(answers, operatorId = DEFAULT_OPERATOR) {
  const weights = weightsFor(operatorId);
  const questions = getAllQuestions();
  const byDomain = new Map(
    DOMAINS.map((d) => [d.id, { ...d, points: 0, scoredCount: 0, naCount: 0, answeredCount: 0 }])
  );

  for (const [qIndex, optIndex] of answers) {
    const question = questions[qIndex];
    if (!question) continue;
    const option = question.options[optIndex];
    if (!option) continue;

    const bucket = byDomain.get(question.domainId);
    if (!bucket) continue;

    bucket.answeredCount++;

    // A-08: a null score means "not applicable" — it leaves the
    // denominator rather than contributing a fabricated middle value.
    if (option.score === null) {
      bucket.naCount++;
      continue;
    }

    bucket.points += option.score;
    bucket.scoredCount++;
  }

  const domains = [];
  let weightedSum = 0;
  let activeWeight = 0;

  for (const domain of DOMAINS) {
    const bucket = byDomain.get(domain.id);
    const scored = bucket.scoredCount > 0;
    const pct = scored ? (bucket.points / (bucket.scoredCount * 4)) * 100 : null;

    /* The calibrated weight, not domain.weight. Both are carried on the
       result: `weight` is what scored this run, `baseWeight` is what the
       registry says, and the export prints both when they differ so a
       reader can see the instrument was tuned rather than wonder why two
       operators' domain percentages do not line up. */
    const weight = weights[domain.id] ?? domain.weight;

    if (scored) {
      weightedSum += pct * weight;
      activeWeight += weight;
    }

    domains.push({
      ...domain,
      weight,
      baseWeight: domain.weight,
      pct: scored ? Math.round(pct) : null,
      scored,
      scoredCount: bucket.scoredCount,
      naCount: bucket.naCount,
      answeredCount: bucket.answeredCount,
      questionCount: domain.questions.length
    });
  }

  // A-07: renormalise across domains that were actually answered, rather
  // than letting an unanswered domain contribute 0 at full weight.
  const healthIndex = activeWeight > 0 ? Math.round(weightedSum / activeWeight) : null;

  const totalQuestions = questions.length;
  const answeredCount = [...answers.keys()].filter((i) => i < totalQuestions).length;

  return {
    healthIndex,
    operator: operatorId,
    calibration: describeCalibration(operatorId),
    domains,
    ranked: domains.filter((d) => d.scored).sort((a, b) => a.pct - b.pct),
    coverage: {
      answered: answeredCount,
      total: totalQuestions,
      confidence: Math.round((answeredCount / totalQuestions) * 100),
      activeWeight,
      skippedDomains: domains.filter((d) => !d.scored).map((d) => d.name)
    }
  };
}

function ragFor(index) {
  if (index === null) return { label: 'Not enough data', color: 'var(--ca-text-quaternary)' };
  if (index < 45) return { label: 'Critical', color: 'var(--ca-danger)' };
  if (index < 65) return { label: 'Attention required', color: 'var(--ca-warning)' };
  return { label: 'Strong', color: 'var(--ca-positive)' };
}

const RECOMMENDATIONS = {
  customs:
    'Pre-file the IDF before the vessel departs, verify your clearing agent’s KRA licence annually, and track your red-channel rate against a 5% target.',
  fleet:
    'Bring every CVRL current and set renewal alerts, fit NTSA-compliant telematics, and move maintenance records off paper.',
  port:
    'Drive container dwell under five days, rebase pricing on the KPA Tariff 2025, and test rail for standard containers — line-haul is roughly a third of road per ton-km on the Nairobi lane.',
  cost:
    'Calculate cost per ton-km by corridor, attack empty backhaul with demand forecasting, and confirm the 2% RDL is in your quoted landed cost.',
  safety:
    'Hold a current NTSA Operator Licence, document and drill the fatal-accident protocol, and run driver safety programmes at least quarterly.',
  docs:
    'Map HS codes against KRA reference values, brief suppliers on PVoC at order stage, and enforce the ACD reference on your Bill of Lading template.',
  corridor:
    'Compare modes per shipment rather than per contract, write down your cross-border procedure per corridor, and report barriers you hit at tradebarriers.africa.',
  financial:
    'Hold a dedicated customs duty reserve, review insurance against actual exposure annually, and build a funded five-year fleet plan before you need it.'
};

/* ============================================================
   Results view
   ============================================================ */

function renderResults(view) {
  const { content, state } = view;
  const results = calculateResults(state.answers, state.operator);
  const rag = ragFor(results.healthIndex);

  view.progress.style.display = 'none';
  content.className = 'animate-fade-in';
  content.id = 'scorecard-results';
  content.replaceChildren();

  if (results.healthIndex === null) {
    content.innerHTML = html`
      <div class="empty-state">
        <h3>Nothing scored yet</h3>
        <p>Answer at least one question that applies to your operation to see an index.</p>
      </div>
    `;
    const restartBtn = document.createElement('button');
    restartBtn.className = 'btn btn-secondary';
    restartBtn.type = 'button';
    restartBtn.textContent = 'Start the diagnostic';
    restartBtn.addEventListener('click', view.restart);
    content.querySelector('.empty-state').appendChild(restartBtn);
    return;
  }

  /* ---- Headline ---- */
  const head = document.createElement('div');
  head.style.textAlign = 'center';
  head.innerHTML = html`
    <div
      class="badge"
      style="background: color-mix(in srgb, ${rag.color} 12%, transparent); color: ${rag.color};"
    >
      ${rag.label}
    </div>
  `;
  content.appendChild(head);
  content.appendChild(ScoreRing({ score: results.healthIndex, color: rag.color }));

  /* ---- Confidence (A-07) ---- */
  const coverage = results.coverage;
  const confidence = document.createElement('div');
  confidence.className = coverage.confidence === 100 ? 'notice notice-info' : 'notice notice-warning';
  confidence.style.marginBottom = 'var(--ca-space-5)';
  confidence.innerHTML = html`
    <span style="flex-shrink:0; margin-top:2px;">${raw(icon(coverage.confidence === 100 ? 'check' : 'alert', 15))}</span>
    <div style="font-size: var(--ca-text-xs);">
      <strong style="font-weight: var(--ca-weight-medium);">
        Confidence: ${percent(coverage.confidence)}
      </strong>
      — ${coverage.answered} of ${coverage.total} questions answered, and the index is weighted
      across the ${coverage.activeWeight}% of domains you scored.
      ${coverage.skippedDomains.length
        ? html` Not scored: ${coverage.skippedDomains.join(', ')}.`
        : ''}
    </div>
  `;
  content.appendChild(confidence);

  /* ---- Domain bars ---- */
  const bars = document.createElement('section');
  bars.style.margin = 'var(--ca-space-5) 0';
  bars.innerHTML = html`<h2 class="section-title">Maturity by domain</h2>`;
  for (const domain of results.domains) {
    bars.appendChild(
      DomainBar({
        name: domain.name,
        percentage: domain.pct ?? 0,
        color: domain.color,
        weight: domain.weight,
        scored: domain.scored
      })
    );

    // What the sector does, beside what you scored. Five of the eight
    // domains carry this; the other three deliberately carry nothing
    // rather than an unsourced sentence, so the absence is meaningful
    // and the reader can tell the difference.
    if (domain.context) {
      const note = document.createElement('p');
      note.className = 'domain-context';
      note.innerHTML = html`
        ${domain.context}
        <a href="${domain.sourceUrl}" target="_blank" rel="noopener noreferrer">
          ${domain.source}</a
        >, as at ${shortDate(domain.asOf)}.
      `;
      bars.appendChild(note);
    }
  }
  content.appendChild(bars);

  /* ---- Findings ---- */
  const findings = document.createElement('section');
  findings.style.margin = 'var(--ca-space-6) 0';
  findings.innerHTML = html`<h2 class="section-title">Priority findings — weakest first</h2>`;
  results.ranked.slice(0, 3).forEach((domain, i) => {
    findings.appendChild(
      FindingCard({
        rank: i + 1,
        domainName: domain.name,
        percentage: domain.pct,
        weight: domain.weight,
        color: domain.color,
        recommendation: RECOMMENDATIONS[domain.id] || 'Review this domain against current practice.'
      })
    );
  });
  content.appendChild(findings);

  /* ---- Method note ---- */
  const method = document.createElement('div');
  method.className = 'card card-tinted';
  method.innerHTML = html`
    <h3 style="font-size: var(--ca-text-sm); margin-bottom: var(--ca-space-2);">How this is calculated</h3>
    <p style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary);">
      Each answer scores 0–4. A domain’s maturity is its points over the maximum available on the
      questions you scored. The index is the weighted mean of those domains, renormalised over the
      weight you actually covered — so skipping a domain lowers confidence rather than silently
      lowering the score. Options marked “not scored” are removed from the denominator entirely.
    </p>
  `;
  content.appendChild(method);

  /* ---- Actions ---- */
  content.appendChild(
    PrintButton({
      targetId: 'scorecard-results',
      title: 'Corridor Health Scorecard',
      subtitle: `Index ${results.healthIndex}/100 · ${rag.label} · confidence ${coverage.confidence}%`
    })
  );

  const footer = document.createElement('div');
  footer.className = 'no-print';
  footer.style.cssText = 'text-align:center; margin-top: var(--ca-space-4);';
  const restartBtn = document.createElement('button');
  restartBtn.className = 'btn btn-ghost';
  restartBtn.type = 'button';
  restartBtn.textContent = 'Start over';
  restartBtn.addEventListener('click', view.restart);
  footer.appendChild(restartBtn);
  content.appendChild(footer);

  /* ---- Persist AFTER painting, and never into the render path (A-06) ---- */
  void saveRecord('scorecard', {
    healthIndex: results.healthIndex,
    confidence: coverage.confidence,
    domains: results.domains.map((d) => ({ id: d.id, pct: d.pct, weight: d.weight })),
    answers: [...state.answers.entries()]
  });
}
